import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  EMAIL_LAYOUT_KEYS,
  EMAIL_TEMPLATES,
  emailTemplateTokenHint,
  type EmailTemplateId,
} from "@gaole/shared";
import { api } from "../../lib/api";
import { toast } from "../../stores/toast";
import { settingsTabLabel } from "../../config/admin-settings";
import { Button, Input, PillSegmentSwitcher, SettingsCard } from "../../components/ui";
import { MarkdownEditor } from "../../components/MarkdownEditor";

interface Props {
  draft: Record<string, unknown>;
  setValue: (key: string, value: unknown) => void;
  saveMut: { mutate: (keys: string[]) => void; isPending: boolean };
  smtpEnabled: boolean;
}

const LAYOUT_KEYS = [
  EMAIL_LAYOUT_KEYS.footerText,
  EMAIL_LAYOUT_KEYS.primaryColor,
  EMAIL_LAYOUT_KEYS.accentColor,
] as const;

export function EmailTemplatesEditor({ draft, setValue, saveMut, smtpEnabled }: Props) {
  const [templateId, setTemplateId] = useState<EmailTemplateId>("verify_email");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testTo, setTestTo] = useState("");

  const template = useMemo(
    () => EMAIL_TEMPLATES.find((t) => t.id === templateId) ?? EMAIL_TEMPLATES[0],
    [templateId],
  );

  const subject = String(draft[template.subjectKey] ?? template.defaultSubject);
  const body = String(draft[template.bodyKey] ?? template.defaultBody);
  const footerText = String(draft[EMAIL_LAYOUT_KEYS.footerText] ?? "");
  const primaryColor = String(draft[EMAIL_LAYOUT_KEYS.primaryColor] ?? "#5460ED");
  const accentColor = String(draft[EMAIL_LAYOUT_KEYS.accentColor] ?? "#DD54EB");

  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        templateId,
        subject,
        body,
      });
      api<{ html: string }>(`/settings/email-template-preview?${params.toString()}`)
        .then((res) => {
          if (!cancelled) setPreviewHtml(res.html);
        })
        .catch(() => {
          if (!cancelled) setPreviewHtml("");
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [templateId, subject, body]);

  const testMut = useMutation({
    mutationFn: () =>
      api("/settings/email-template-test", {
        method: "POST",
        body: { templateId, to: testTo.trim(), subject, body },
      }),
    onSuccess: () => toast("success", "Test email sent"),
    onError: (e: Error) => toast("error", e.message),
  });

  return (
    <div className="w-full min-w-0 space-y-5">
      <div>
        <h1 className="font-display text-lg font-bold">{settingsTabLabel("email_templates")}</h1>
        <p className="mt-0.5 text-xs text-muted">
          Markdown templates for transactional emails. Logo comes from Settings → Company. Use{" "}
          {emailTemplateTokenHint(templateId)} in the body.
        </p>
      </div>

      <PillSegmentSwitcher
        value={templateId}
        onChange={(v) => setTemplateId(v as EmailTemplateId)}
        options={EMAIL_TEMPLATES.map((t) => ({ value: t.id, label: t.title }))}
      />

      <SettingsCard
        title={template.title}
        description={`Tokens: ${emailTemplateTokenHint(templateId)}. Markdown links render as CTA buttons.`}
        footer={
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate([template.subjectKey, template.bodyKey])}
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Subject"
            value={subject}
            onChange={(e) => setValue(template.subjectKey, e.target.value)}
          />
          <MarkdownEditor
            label="Body"
            value={body}
            onChange={(v) => setValue(template.bodyKey, v)}
            hint="Use Markdown. Put action links on their own line, e.g. [Verify email]({{verifyUrl}})."
          />
        </div>
      </SettingsCard>

      <SettingsCard
        title="Email layout"
        description="Shared header/footer styling for all transactional emails."
        footer={
          <div className="flex justify-end">
            <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate([...LAYOUT_KEYS])}>
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Footer text"
            value={footerText}
            onChange={(e) => setValue(EMAIL_LAYOUT_KEYS.footerText, e.target.value)}
          />
          <div className="form-row-2">
            <Input
              label="Primary color"
              value={primaryColor}
              onChange={(e) => setValue(EMAIL_LAYOUT_KEYS.primaryColor, e.target.value)}
              hint="CTA buttons"
            />
            <Input
              label="Accent color"
              value={accentColor}
              onChange={(e) => setValue(EMAIL_LAYOUT_KEYS.accentColor, e.target.value)}
              hint="Text logo fallback"
            />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Preview" description="Rendered email with sample data and your current draft.">
        {previewLoading && !previewHtml ? (
          <p className="text-xs text-muted">Loading preview…</p>
        ) : (
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            className="h-[520px] w-full rounded-xl border border-line bg-white"
            sandbox=""
          />
        )}
      </SettingsCard>

      <SettingsCard
        title="Send test"
        description={
          smtpEnabled
            ? "Sends this template with sample placeholder data."
            : "Enable SMTP under Settings → SMTP before sending tests."
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <Input
              label="Recipient"
              type="email"
              placeholder="you@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!smtpEnabled || !testTo.trim() || testMut.isPending}
            onClick={() => testMut.mutate()}
          >
            Send test
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted">
          Subject is prefixed with [Test]. Tests use the current editor values (save to persist defaults).
        </p>
      </SettingsCard>
    </div>
  );
}
